import * as React from 'react';
import { useSelector } from 'react-redux';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import useDebouncedState from 'hooks/useDebouncedState';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { PrimaryButton } from '@fluentui/react';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import moment from 'moment-timezone';
import { SelectOption } from 'formComponents/Select/types';
import { ValueType } from 'react-select';
import { Select } from 'formComponents/Select/Select';
import { useBetreuer, useBetreuerByNr } from 'api/betreuer';
import { authSelector } from 'store/auth/selector';
import { Label } from '@fluentui/react/lib/Label';
import { AuftragStatus } from 'formComponents/lookups/AuftragStatus';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { generateReportPrintUrl } from '../utils';
import { Berechnungsart, defaultFilterValues, FilterValues } from './types';
import { Rahmenauftraege } from './Rahmenauftraege';

const berechnungsartOptions = [
    {
        value: 'offen',
        label: 'Offen am Rahmen',
    },
    {
        value: 'abrufe',
        label: 'Summe Abrufe',
    },
];

const styles = mergeStyleSets({
    container: {
        margin: 0,
        marginTop: 5,
        padding: 0,
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 5,
        height: 'calc(100vh - 75px)',
    },
    searchLine1: {
        display: 'grid',
        gridTemplateColumns: '140px 140px 180px 180px 180px 160px max-content 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    actionButtons: {
        marginTop: 29,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, max-content)',
        gridColumnGap: 10,
    },
    reset: {
        textAlign: 'right',
        marginTop: 8,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

export const RahmenauftraegeView: React.FC = () => {
    const auth = useSelector(authSelector);
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const betreuerByNr = useBetreuerByNr();
    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(
        {
            ...defaultFilterValues,
        },
        2000,
    );

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.searchLine1}>
                <FormDatePicker
                    value={filterValues.datumVon}
                    label="Datum Von (Auftrag)"
                    options={{
                        required: true,
                    }}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                        });
                    }}
                />

                <FormDatePicker
                    value={filterValues.datumBis}
                    label="Datum Bis (Auftrag)"
                    options={{
                        required: true,
                    }}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                        });
                    }}
                />

                <div>
                    <Label>Betreuer (Auftrag)</Label>
                    <Select
                        name="betreuer"
                        value={
                            filterValues.betreuer
                                ? {
                                      value: filterValues.betreuer.toString(),
                                      label: betreuerByNr[filterValues.betreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                betreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Tel. Betreuer (Auftrag)</Label>
                    <Select
                        name="telBetreuer"
                        value={
                            filterValues.telBetreuer
                                ? {
                                      value: filterValues.telBetreuer.toString(),
                                      label: betreuerByNr[filterValues.telBetreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                telBetreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Status</Label>
                    <AuftragStatus
                        renderLabel="no"
                        addAll
                        value={filterValues.status?.toString() || '__all__'}
                        onChange={(newValue): void => {
                            setFilterValues({
                                ...filterValues,
                                status: newValue === '__all__' ? undefined : parseInt(newValue, 10),
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Berechnungsart</Label>
                    <Select
                        name="status"
                        value={berechnungsartOptions.find((o) => o.value === filterValues.berechnungsart)}
                        options={berechnungsartOptions}
                        onChange={(option: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                berechnungsart: (option?.value || 'offen') as Berechnungsart,
                            });
                        }}
                    />
                </div>

                <Checkbox
                    className={formStyles.checkboxInsideFormAligned}
                    label="Ohne Reclay"
                    checked={filterValues.excludeReclay}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            excludeReclay: !!checked,
                        });
                    }}
                />

                <div />

                <div className={styles.actionButtons}>
                    {auth.rolle !== 'AD' && auth.rolle !== 'EX' && (
                        <PrimaryButton
                            onClick={() => {
                                window.open(
                                    generateReportPrintUrl('rahmenauftraege', {
                                        sortColumn: filterValues.sortColumn,
                                        sortDirection: filterValues.sortDirection,
                                        datumVon: filterValues.datumVon,
                                        datumBis: filterValues.datumBis,
                                        betreuer: filterValues.betreuer,
                                        telBetreuer: filterValues.telBetreuer,
                                        status: filterValues.status,
                                        berechnungsart: filterValues.berechnungsart,
                                        excludeReclay: filterValues.excludeReclay,
                                    }),
                                );
                            }}
                            text="Drucken"
                        />
                    )}
                </div>
            </div>

            <Rahmenauftraege
                sortColumn={filterValues.sortColumn}
                sortDirection={filterValues.sortDirection}
                toggleSort={(sortColumn) => {
                    if (filterValues.sortColumn === sortColumn) {
                        setFilterValues({
                            ...filterValues,
                            sortColumn,
                            sortDirection: filterValues.sortDirection === 'asc' ? 'desc' : 'asc',
                        });
                    } else {
                        setFilterValues({ ...filterValues, sortColumn, sortDirection: 'desc' });
                    }
                }}
                datumVon={debouncedFilterValues.datumVon}
                datumBis={debouncedFilterValues.datumBis}
                betreuer={debouncedFilterValues.betreuer}
                telBetreuer={debouncedFilterValues.telBetreuer}
                status={debouncedFilterValues.status}
                berechnungsart={debouncedFilterValues.berechnungsart}
                excludeReclay={debouncedFilterValues.excludeReclay}
            />
        </div>
    );
};
